import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  ArrowLeft, Save, Loader2, Store, Settings, BarChart3, Building2,
  Globe, Palette, DollarSign, Star, Link, Mail, Phone, Search,
  CheckCircle, XCircle, Eye, EyeOff, TrendingUp, Users, ShoppingBag
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MarketplaceAdminPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  
  // Settings state
  const [settings, setSettings] = useState({
    enabled: false,
    name: 'iStayBnB',
    tagline: 'Find your perfect stay',
    logo_url: '',
    primary_color: '#FF385C',
    commission_percent: 10.0,
    min_commission: 5.0,
    currency: 'KES',
    featured_property_ids: [],
    meta_title: 'iStayBnB - Vacation Rentals & Unique Stays',
    meta_description: 'Discover and book unique accommodations around the world',
    support_email: '',
    support_phone: '',
    social_links: {},
    show_host_info: true,
    require_guest_account: false,
    terms_url: '',
    privacy_url: ''
  });
  
  // Stats state
  const [stats, setStats] = useState({
    listed_properties: 0,
    total_bookings: 0,
    total_revenue: 0,
    total_commission: 0,
    bookings_this_week: 0
  });
  
  // Properties state
  const [properties, setProperties] = useState([]);
  const [propertiesPage, setPropertiesPage] = useState(1);
  const [propertiesTotalPages, setPropertiesTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  
  const token = localStorage.getItem('ihostpms_admin_token');
  
  const loadSettings = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/marketplace/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.error('Load settings error:', err);
    }
  }, [token]);
  
  const loadStats = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/marketplace/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        setStats(await resp.json());
      }
    } catch (err) {
      console.error('Load stats error:', err);
    }
  }, [token]);
  
  const loadProperties = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/marketplace/admin/properties?page=${propertiesPage}&limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setProperties(data.properties || []);
        setPropertiesTotalPages(Math.ceil((data.total || 0) / 20));
      }
    } catch (err) {
      console.error('Load properties error:', err);
    }
  }, [token, propertiesPage]);
  
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadSettings(), loadStats(), loadProperties()]);
      setLoading(false);
    };
    init();
  }, [loadSettings, loadStats, loadProperties]);
  
  const handleSaveSettings = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/marketplace/admin/settings`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(settings)
      });
      
      if (resp.ok) {
        setSaveResult({ success: true, message: 'Settings saved successfully' });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (err) {
      setSaveResult({ success: false, message: err.message });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveResult(null), 3000);
    }
  };
  
  const togglePropertyListing = async (propertyId, listed) => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/marketplace/admin/properties/${propertyId}/listing?listed=${listed}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (resp.ok) {
        setProperties(props => props.map(p => 
          p.property_id === propertyId ? { ...p, marketplace_listed: listed } : p
        ));
        loadStats();
      }
    } catch (err) {
      console.error('Toggle listing error:', err);
    }
  };
  
  const toggleFeatured = (propertyId) => {
    setSettings(prev => {
      const featured = prev.featured_property_ids || [];
      const newFeatured = featured.includes(propertyId)
        ? featured.filter(id => id !== propertyId)
        : [...featured, propertyId];
      return { ...prev, featured_property_ids: newFeatured };
    });
  };
  
  const filteredProperties = properties.filter(p => 
    !searchQuery || 
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.tenant_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const TABS = [
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'properties', label: 'Properties', icon: Building2 },
    { id: 'stats', label: 'Analytics', icon: BarChart3 },
  ];
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="marketplace-admin-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <Store className="w-6 h-6 text-[#FF385C]" />
                  Marketplace Settings
                </h1>
                <p className="text-sm text-slate-500">Manage iStayBnB public marketplace</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                settings.enabled 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'bg-slate-100 text-slate-500'
              }`}>
                {settings.enabled ? (
                  <><Eye className="w-4 h-4" /> Live</>
                ) : (
                  <><EyeOff className="w-4 h-4" /> Offline</>
                )}
              </div>
              <Button 
                onClick={() => window.open('/marketplace', '_blank')}
                variant="outline"
                className="gap-2"
              >
                <Globe className="w-4 h-4" /> View Marketplace
              </Button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4 -mb-px">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#FF385C] text-[#FF385C]'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-8">
            {/* Enable/Disable */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">Marketplace Status</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Enable or disable the public marketplace
                  </p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings(s => ({ ...s, enabled: checked }))}
                  data-testid="marketplace-enabled"
                />
              </div>
              {!settings.enabled && (
                <div className="mt-4 p-4 bg-amber-50 rounded-xl text-amber-800 text-sm">
                  The marketplace is currently offline. Visitors will see an "unavailable" message.
                </div>
              )}
            </div>
            
            {/* Branding */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-6 flex items-center gap-2">
                <Palette className="w-5 h-5 text-[#FF385C]" /> Branding
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Marketplace Name</Label>
                  <Input
                    value={settings.name}
                    onChange={(e) => setSettings(s => ({ ...s, name: e.target.value }))}
                    placeholder="iStayBnB"
                    className="mt-1"
                    data-testid="marketplace-name"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Tagline</Label>
                  <Input
                    value={settings.tagline}
                    onChange={(e) => setSettings(s => ({ ...s, tagline: e.target.value }))}
                    placeholder="Find your perfect stay"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Logo URL</Label>
                  <Input
                    value={settings.logo_url}
                    onChange={(e) => setSettings(s => ({ ...s, logo_url: e.target.value }))}
                    placeholder="https://..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Primary Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => setSettings(s => ({ ...s, primary_color: e.target.value }))}
                      className="w-14 h-10 p-1"
                    />
                    <Input
                      value={settings.primary_color}
                      onChange={(e) => setSettings(s => ({ ...s, primary_color: e.target.value }))}
                      placeholder="#FF385C"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Commission */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-6 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#FF385C]" /> Commission Settings
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Commission Percentage</Label>
                  <div className="relative mt-1">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="50"
                      value={settings.commission_percent}
                      onChange={(e) => setSettings(s => ({ ...s, commission_percent: parseFloat(e.target.value) }))}
                      className="pr-8"
                      data-testid="commission-percent"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Platform fee charged on each booking</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Minimum Commission</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={settings.min_commission}
                    onChange={(e) => setSettings(s => ({ ...s, min_commission: parseFloat(e.target.value) }))}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Minimum fee per booking</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Default Currency</Label>
                  <select
                    value={settings.currency}
                    onChange={(e) => setSettings(s => ({ ...s, currency: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 border border-slate-200 rounded-md bg-white"
                  >
                    <option value="KES">KES - Kenyan Shilling</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* SEO */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-6 flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#FF385C]" /> SEO & Meta
              </h2>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Meta Title</Label>
                  <Input
                    value={settings.meta_title}
                    onChange={(e) => setSettings(s => ({ ...s, meta_title: e.target.value }))}
                    placeholder="iStayBnB - Vacation Rentals"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Meta Description</Label>
                  <Textarea
                    value={settings.meta_description}
                    onChange={(e) => setSettings(s => ({ ...s, meta_description: e.target.value }))}
                    placeholder="Discover and book unique accommodations..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </div>
            </div>
            
            {/* Contact */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-6 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#FF385C]" /> Contact & Support
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Support Email</Label>
                  <Input
                    type="email"
                    value={settings.support_email}
                    onChange={(e) => setSettings(s => ({ ...s, support_email: e.target.value }))}
                    placeholder="support@istaybnb.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Support Phone</Label>
                  <Input
                    value={settings.support_phone}
                    onChange={(e) => setSettings(s => ({ ...s, support_phone: e.target.value }))}
                    placeholder="+254 700 000000"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Terms of Service URL</Label>
                  <Input
                    value={settings.terms_url}
                    onChange={(e) => setSettings(s => ({ ...s, terms_url: e.target.value }))}
                    placeholder="https://istaybnb.com/terms"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Privacy Policy URL</Label>
                  <Input
                    value={settings.privacy_url}
                    onChange={(e) => setSettings(s => ({ ...s, privacy_url: e.target.value }))}
                    placeholder="https://istaybnb.com/privacy"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Options */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-6 flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#FF385C]" /> Options
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <p className="font-medium text-slate-800">Show Host Information</p>
                    <p className="text-sm text-slate-500">Display host name on property listings</p>
                  </div>
                  <Switch
                    checked={settings.show_host_info}
                    onCheckedChange={(checked) => setSettings(s => ({ ...s, show_host_info: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <p className="font-medium text-slate-800">Require Guest Account</p>
                    <p className="text-sm text-slate-500">Guests must create account to book</p>
                  </div>
                  <Switch
                    checked={settings.require_guest_account}
                    onCheckedChange={(checked) => setSettings(s => ({ ...s, require_guest_account: checked }))}
                  />
                </div>
              </div>
            </div>
            
            {/* Save Button */}
            {saveResult && (
              <div className={`flex items-center gap-2 p-4 rounded-xl ${
                saveResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
              }`}>
                {saveResult.success ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                {saveResult.message}
              </div>
            )}
            
            <Button
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-[#FF385C] hover:bg-[#E31C5F] text-white rounded-xl px-8"
              data-testid="save-marketplace-settings"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        )}
        
        {/* Properties Tab */}
        {activeTab === 'properties' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-800">Manage Property Listings</h2>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search properties..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Property</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Host</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Location</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Price</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Listed</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Featured</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProperties.map((prop) => (
                      <tr key={prop.property_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <p className="font-medium text-slate-800">{prop.name}</p>
                          <p className="text-xs text-slate-500">{prop.property_id}</p>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{prop.tenant_name}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{prop.city}, {prop.country}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {prop.base_price?.toLocaleString()} /night
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Switch
                            checked={prop.marketplace_listed || false}
                            onCheckedChange={(checked) => togglePropertyListing(prop.property_id, checked)}
                          />
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => toggleFeatured(prop.property_id)}
                            disabled={!prop.marketplace_listed}
                            className={`p-2 rounded-lg transition-colors ${
                              settings.featured_property_ids?.includes(prop.property_id)
                                ? 'bg-amber-100 text-amber-600'
                                : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                            } ${!prop.marketplace_listed ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <Star className={`w-4 h-4 ${
                              settings.featured_property_ids?.includes(prop.property_id) ? 'fill-amber-400' : ''
                            }`} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {propertiesTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={propertiesPage === 1}
                    onClick={() => setPropertiesPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-slate-600">
                    Page {propertiesPage} of {propertiesTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={propertiesPage === propertiesTotalPages}
                    onClick={() => setPropertiesPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
            
            <p className="text-sm text-slate-500">
              Note: Don't forget to save settings after marking properties as featured.
            </p>
          </div>
        )}
        
        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-500">Listed Properties</span>
                </div>
                <p className="text-3xl font-bold text-slate-800">{stats.listed_properties}</p>
              </div>
              
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-500">Total Bookings</span>
                </div>
                <p className="text-3xl font-bold text-slate-800">{stats.total_bookings}</p>
              </div>
              
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-500">Total Revenue</span>
                </div>
                <p className="text-3xl font-bold text-slate-800">
                  {settings.currency} {stats.total_revenue?.toLocaleString()}
                </p>
              </div>
              
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-500">Commission Earned</span>
                </div>
                <p className="text-3xl font-bold text-slate-800">
                  {settings.currency} {stats.total_commission?.toLocaleString()}
                </p>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">This Week</h3>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#FF385C]/10 rounded-xl flex items-center justify-center">
                    <ShoppingBag className="w-6 h-6 text-[#FF385C]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats.bookings_this_week}</p>
                    <p className="text-sm text-slate-500">New bookings</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
